import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, X, Inbox, Link2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import {
  API_BASE, DETECTED_LABELS, detectedList, EVENT_LABELS, EVENT_TONE, evLabel, chLabel, chColor, CONSENT, ORIGIN, nf, money, fmtTime, daysAgo, pathOf,
} from './shared';

const PAGE = 50;

const Badge = ({ cls, children, title }) => (
  <span title={title} className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[11px] font-medium whitespace-nowrap ${cls}`}>{children}</span>
);
const EvBadge = ({ name }) => (
  <span className="inline-flex items-baseline gap-1 whitespace-nowrap">
    <Badge cls={EVENT_TONE[name] || 'bg-gray-100 text-gray-700 border-gray-200'}>{evLabel(name)}</Badge>
    <span className="text-[10px] text-gray-400">{name}</span>
  </span>
);
const Ch = ({ o }) => (o?.channel || o?.source) ? (
  <div className="min-w-[110px]">
    <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: chColor(o.channel) }} /><span className="text-xs text-gray-900">{chLabel(o.channel)}</span></div>
    <div className="text-[11px] text-gray-500 truncate max-w-[160px]">{[o.source, o.medium].filter(Boolean).join(' / ')}</div>
  </div>
) : <span className="text-gray-300">-</span>;

const DetBadges = ({ v }) => {
  const l = detectedList(v);
  if (!l.length) return null;
  return <span className="inline-flex flex-wrap gap-1">{l.map((d) => <Badge key={d} cls="bg-gray-50 text-gray-600 border-gray-200" title={d}>{DETECTED_LABELS[d] || d}</Badge>)}</span>;
};
const consentTip = (e) => (e.params && e.params.consent_source ? `İzin kaynağı: ${e.params.consent_source}` : undefined);

const itemsSummary = (items) => {
  if (!Array.isArray(items) || !items.length) return null;
  const first = items[0]?.name || items[0]?.sku || items[0]?.item_id || 'ürün';
  return items.length > 1 ? `${first} +${items.length - 1}` : first;
};
const counters = (c) => c ? `${c.session_count ?? 0}. oturum · ${c.session_pageview_count ?? 0} sayfa · ${c.session_event_count ?? 0} olay` : '-';

async function getJson(url) {
  const r = await fetch(url, { credentials: 'include' });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json();
}

function Timeline({ brandId, visitor, from, to, includeTest, onClose }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let alive = true;
    setRows(null); setErr('');
    (async () => {
      try {
        const all = []; let offset = 0; let total = 0;
        do {
          const j = await getJson(`${API_BASE}/events?brandId=${brandId}&from=${from}&to=${to}&includeTest=${includeTest ? 1 : 0}&visitorId=${encodeURIComponent(visitor)}&limit=1000&offset=${offset}`);
          all.push(...(j.data || [])); total = j.meta?.total || 0; offset += 1000;
        } while (all.length < total && offset < 5000);
        if (alive) setRows(all.sort((a, b) => new Date(a.event_at) - new Date(b.event_at) || a.id - b.id));
      } catch (e) { if (alive) setErr(e.message); }
    })();
    return () => { alive = false; };
  }, [brandId, visitor, from, to, includeTest]);

  let lastSession = null;
  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-gray-200 bg-gray-50">
        <div className="min-w-0">
          <div className="text-sm font-medium text-gray-900">Ziyaretçi akışı</div>
          <div className="text-[11px] text-gray-500 break-all">{visitor}</div>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <button onClick={() => { try { navigator.clipboard.writeText(window.location.href); } catch (e) { /* yoksay */ } }} className="inline-flex items-center gap-1 px-2 py-1 border border-gray-300 rounded-lg bg-white hover:bg-gray-50"><Link2 size={12} />Bağlantıyı kopyala</button>
          <button onClick={onClose} className="inline-flex items-center gap-1 px-2 py-1 border border-gray-300 rounded-lg bg-white hover:bg-gray-50"><X size={12} />Kapat</button>
        </div>
      </div>
      {err && <div className="m-4 p-3 rounded-lg bg-rose-50 border border-rose-200 text-sm text-rose-700">Akış alınamadı: {err}</div>}
      {!rows && !err && <div className="p-8 flex justify-center text-gray-400"><Loader2 className="animate-spin" size={18} /></div>}
      {rows && rows.length === 0 && <div className="p-8 text-center text-sm text-gray-500">Bu ziyaretçi için seçili aralıkta olay yok.</div>}
      {rows && rows.length > 0 && (
        <ol className="p-4 space-y-0">
          {rows.map((e, i) => {
            const sep = e.session_id !== lastSession; lastSession = e.session_id;
            const org = ORIGIN[e.origin] || ORIGIN.browser; const cs = CONSENT[e.consent] || CONSENT.unknown;
            const its = Array.isArray(e.items) ? e.items : [];
            return (
              <React.Fragment key={e.id ?? i}>
                {sep && (
                  <li className="flex items-center gap-3 py-2 text-[11px] text-gray-500">
                    <span className="h-px flex-1 bg-gray-200" />
                    <span>Oturum <span className="text-gray-400">{e.session_id ? String(e.session_id).slice(0, 8) : '-'}</span> · {e.counters?.session_count ?? '?'}. oturum</span>
                    <span className="h-px flex-1 bg-gray-200" />
                  </li>
                )}
                <li className="flex gap-3">
                  <div className="flex flex-col items-center"><span className="w-2.5 h-2.5 rounded-full bg-gray-900 mt-1.5" /><span className="w-px flex-1 bg-gray-200" /></div>
                  <div className="pb-4 min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[11px] text-gray-500 tabular-nums">{fmtTime(e.event_at)}</span>
                      <EvBadge name={e.event_name} />
                      <Badge cls={org.cls} title={`Tarayıcı ${e.browser_at ? fmtTime(e.browser_at) : '-'} · Sunucu ${e.server_at ? fmtTime(e.server_at) : '-'}`}>{org.label}</Badge>
                      <Badge cls={cs.cls} title={consentTip(e)}>{cs.label}</Badge>
                      <DetBadges v={e.detected_by} />
                      {e.is_test && <Badge cls="bg-amber-50 text-amber-700 border-amber-200">Test</Badge>}
                      {e.order_id && <Badge cls="bg-emerald-50 text-emerald-700 border-emerald-200">Sipariş #{e.order_id}</Badge>}
                      {e.value != null && <span className="text-xs font-medium tabular-nums text-gray-900">{money(e.value, e.currency)}</span>}
                    </div>
                    <div className="text-xs text-gray-600 mt-1 break-all">{pathOf(e.page_url)}{e.page_type ? <span className="text-gray-400"> · {e.page_type}</span> : null}{e.search_term ? <span> · "{e.search_term}"</span> : null}</div>
                    <div className="flex flex-wrap gap-x-6 gap-y-1 mt-1.5 text-[11px] text-gray-500">
                      <span className="inline-flex items-center gap-1">A: <Ch o={e.a} /></span>
                      <span className="inline-flex items-center gap-1">B: <Ch o={e.b} /></span>
                    </div>
                    {its.length > 0 && (
                      <ul className="mt-1.5 text-[11px] text-gray-700 space-y-0.5">
                        {its.map((it, k) => <li key={k}>{it.quantity ?? 1} x {it.name || it.sku || it.item_id}{it.variant ? ` (${it.variant})` : ''}{it.price != null ? ` · ${money(it.price, e.currency)}` : ''}</li>)}
                      </ul>
                    )}
                  </div>
                </li>
              </React.Fragment>
            );
          })}
        </ol>
      )}
    </div>
  );
}

export default function EventsTab() {
  const brandId = useAuth().selectedBrand?.id;
  const [sp, setSp] = useSearchParams();
  const visitor = sp.get('visitor') || '';
  const [from, setFrom] = useState(daysAgo(29));
  const [to, setTo] = useState(daysAgo(0));
  const [includeTest, setIncludeTest] = useState(true);
  const [eventName, setEventName] = useState('');
  const [visitorQ, setVisitorQ] = useState('');
  const [detected, setDetected] = useState('');
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const setVisitor = (v) => {
    const n = new URLSearchParams(sp); n.set('tab', 'olaylar');
    if (v) n.set('visitor', v); else n.delete('visitor');
    setSp(n);
  };

  const load = useCallback(async () => {
    if (!brandId) return;
    setLoading(true); setError('');
    try {
      const qs = new URLSearchParams({ brandId, from, to, includeTest: includeTest ? '1' : '0', limit: String(PAGE), offset: String(page * PAGE) });
      if (eventName) qs.set('eventName', eventName);
      if (visitorQ.trim()) qs.set('visitorId', visitorQ.trim());
      if (detected) qs.set('detectedBy', detected);
      const j = await getJson(`${API_BASE}/events?${qs}`);
      setRows(j.data || []); setTotal(j.meta?.total || 0);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, [brandId, from, to, includeTest, eventName, visitorQ, detected, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(0); }, [from, to, includeTest, eventName, visitorQ, detected]);

  const pages = Math.max(1, Math.ceil(total / PAGE));
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <input type="date" value={from} max={to} onChange={(e) => e.target.value && setFrom(e.target.value)} className="border border-gray-300 rounded-lg px-2 py-1.5 bg-white" />
        <span className="text-gray-400">-</span>
        <input type="date" value={to} min={from} onChange={(e) => e.target.value && setTo(e.target.value)} className="border border-gray-300 rounded-lg px-2 py-1.5 bg-white" />
        <select value={eventName} onChange={(e) => setEventName(e.target.value)} className="border border-gray-300 rounded-lg px-2 py-1.5 bg-white">
          <option value="">Tüm olaylar</option>
          {Object.keys(EVENT_LABELS).map((k) => <option key={k} value={k}>{EVENT_LABELS[k]} ({k})</option>)}
        </select>
        <select value={detected} onChange={(e) => setDetected(e.target.value)} className="border border-gray-300 rounded-lg px-2 py-1.5 bg-white" title="Olayın nereden tespit edildiği (detected_by)">
          <option value="">Tüm kaynaklar</option>
          {Object.keys(DETECTED_LABELS).map((k) => <option key={k} value={k}>{DETECTED_LABELS[k]} ({k})</option>)}
        </select>
        <input value={visitorQ} onChange={(e) => setVisitorQ(e.target.value)} placeholder="Ziyaretçi kimliği ara" className="border border-gray-300 rounded-lg px-2 py-1.5 bg-white w-56" />
        <label className="flex items-center gap-1.5 cursor-pointer select-none" title="Prototip testi sırasında açık bırakıldı">
          <input type="checkbox" checked={includeTest} onChange={(e) => setIncludeTest(e.target.checked)} />Test olaylarını göster
        </label>
      </div>

      {visitor && <Timeline brandId={brandId} visitor={visitor} from={from} to={to} includeTest={includeTest} onClose={() => setVisitor('')} />}

      {error && <div className="p-3 rounded-lg bg-rose-50 border border-rose-200 text-sm text-rose-700">Veri alınamadı: {error}</div>}

      <div className={`bg-white border border-gray-200 rounded-xl overflow-hidden ${loading ? 'opacity-60' : ''}`}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div className="text-sm font-medium text-gray-900">Ham olaylar <span className="text-gray-400 font-normal tabular-nums">({nf.format(total)})</span></div>
          <div className="flex items-center gap-2 text-xs text-gray-600">
            <button disabled={page === 0} onClick={() => setPage(page - 1)} className="px-2 py-1 border border-gray-300 rounded-lg disabled:opacity-40">Önceki</button>
            <span className="tabular-nums">{page + 1} / {pages}</span>
            <button disabled={page + 1 >= pages} onClick={() => setPage(page + 1)} className="px-2 py-1 border border-gray-300 rounded-lg disabled:opacity-40">Sonraki</button>
          </div>
        </div>
        {loading && rows.length === 0 ? (
          <div className="p-10 flex justify-center text-gray-400"><Loader2 className="animate-spin" size={18} /></div>
        ) : rows.length === 0 && !error ? (
          <div className="py-12 px-6 text-center">
            <Inbox className="mx-auto text-gray-300 mb-2" size={30} />
            <div className="text-sm font-medium text-gray-800">Bu aralıkta olay yok</div>
            <p className="text-xs text-gray-500 mt-1">Sitede gezinildikçe ve sipariş verildikçe olaylar buraya düşer. Tarih aralığını ya da filtreleri değiştirmeyi dene.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-gray-50 text-gray-500">
                <tr>{['Zaman', 'Olay', 'Köken', 'Tespit', 'İzin', 'Ziyaretçi', 'Oturum', 'Sayfa', 'A kanalı', 'B kanalı', 'Değer', 'Ürünler', 'Sayaçlar'].map((h) => <th key={h} className="text-left font-medium px-3 py-2 whitespace-nowrap">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((e) => {
                  const org = ORIGIN[e.origin] || ORIGIN.browser; const cs = CONSENT[e.consent] || CONSENT.unknown;
                  return (
                    <tr key={e.id} className="hover:bg-gray-50 align-top">
                      <td className="px-3 py-2 whitespace-nowrap tabular-nums text-gray-700">{fmtTime(e.event_at)}{e.is_test && <div><Badge cls="bg-amber-50 text-amber-700 border-amber-200">Test</Badge></div>}</td>
                      <td className="px-3 py-2"><EvBadge name={e.event_name} />{e.order_id && <div className="text-[11px] text-gray-500 mt-0.5">Sipariş #{e.order_id}</div>}</td>
                      <td className="px-3 py-2"><Badge cls={org.cls}>{org.label}</Badge></td>
                      <td className="px-3 py-2"><DetBadges v={e.detected_by} /></td>
                      <td className="px-3 py-2"><Badge cls={cs.cls} title={consentTip(e)}>{cs.label}</Badge></td>
                      <td className="px-3 py-2 whitespace-nowrap">{e.visitor_id ? <button onClick={() => setVisitor(e.visitor_id)} className="text-indigo-700 hover:underline" title="Bu ziyaretçinin akışını aç">{String(e.visitor_id).slice(0, 8)}…</button> : '-'}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-gray-500">{e.session_id ? `${String(e.session_id).slice(0, 8)}…` : '-'}</td>
                      <td className="px-3 py-2 max-w-[220px]"><div className="truncate text-gray-800" title={e.page_url || ''}>{pathOf(e.page_url)}</div>{e.page_type && <div className="text-[11px] text-gray-400">{e.page_type}</div>}</td>
                      <td className="px-3 py-2"><Ch o={e.a} /></td>
                      <td className="px-3 py-2"><Ch o={e.b} /></td>
                      <td className="px-3 py-2 whitespace-nowrap tabular-nums text-right">{e.value != null ? money(e.value, e.currency) : <span className="text-gray-300">-</span>}</td>
                      <td className="px-3 py-2 max-w-[200px]"><div className="truncate text-gray-700" title={itemsSummary(e.items) || ''}>{itemsSummary(e.items) || <span className="text-gray-300">-</span>}</div></td>
                      <td className="px-3 py-2 whitespace-nowrap text-[11px] text-gray-500">{counters(e.counters)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
